import { Link } from "react-router-dom";
import FairPriceBadge from "../automap/FairPriceBadge";
import { RiskLevelPill } from "../automap/RiskBadge";
import DealerListingPublicAction from "./DealerListingPublicAction";
import { FAIR_PRICE_LABELS_ES } from "../../constants/automapFairPriceConstants";
import { qualityBadgeClass } from "../../constants/automapDealerInventoryConstants";

function primaryPhoto(listing) {
  const photo = listing.photos?.find((entry) => entry.isPrimary) || listing.photos?.[0];
  return photo?.url || null;
}

function listingTitle(listing) {
  return [listing.year, listing.make, listing.model, listing.version].filter(Boolean).join(" ");
}

function statusTone(status) {
  if (status === "active") return "active";
  if (status === "sold" || status === "archived") return "muted";
  if (status === "paused") return "paused";
  return "neutral";
}

export default function DealerPortalInventoryCard({
  listing,
  fairPriceInsight,
  riskInsight,
  formatMoney,
  locale,
  t,
  formatListingStatus,
  onAction,
  onDuplicate,
  dealerId = null,
}) {
  const photoUrl = primaryPhoto(listing);
  const fairPriceLabel = fairPriceInsight
    ? FAIR_PRICE_LABELS_ES[fairPriceInsight.fairPriceLabel] || "—"
    : null;

  return (
    <article className="dealer-portal-inventory-card">
      <Link
        className="dealer-portal-inventory-card__media"
        to={`/v/automap/dealer/inventario/${listing.id}`}
        aria-label={listingTitle(listing)}
      >
        {photoUrl ? (
          <img src={photoUrl} alt="" loading="lazy" />
        ) : (
          <div className="dealer-portal-inventory-card__placeholder">{t("inventory.noPhoto")}</div>
        )}
        <span className={`dealer-portal-inventory-card__status dealer-portal-inventory-card__status--${statusTone(listing.status)}`}>
          {formatListingStatus(listing.status)}
        </span>
      </Link>

      <div className="dealer-portal-inventory-card__body">
        <div className="dealer-portal-inventory-card__head">
          <h3 className="dealer-portal-inventory-card__title">
            <Link to={`/v/automap/dealer/inventario/${listing.id}`}>{listingTitle(listing)}</Link>
          </h3>
          <p className="dealer-portal-inventory-card__price">{formatMoney(listing.priceCop)}</p>
        </div>

        <dl className="dealer-portal-inventory-card__specs">
          <div>
            <dt>{t("inventory.mileage")}</dt>
            <dd>{listing.mileageKm != null ? listing.mileageKm.toLocaleString(locale) : "—"}</dd>
          </div>
          <div>
            <dt>{t("inventory.city")}</dt>
            <dd>{listing.city || "—"}</dd>
          </div>
          <div>
            <dt>{t("inventory.availability")}</dt>
            <dd>{formatListingStatus(listing.availabilityStatus)}</dd>
          </div>
        </dl>

        <div className="dealer-portal-inventory-card__insights">
          <span className={`admin-badge ${qualityBadgeClass(listing.qualityScore)}`}>
            {listing.qualityScore} · {listing.qualityLabel}
          </span>
          {fairPriceInsight ? (
            <span className="dealer-portal-inventory-card__insight">
              <FairPriceBadge listing={fairPriceInsight} size="small" variant="compact" />
              <span className="dealer-portal-inventory-card__insight-label">{fairPriceLabel}</span>
            </span>
          ) : null}
          {riskInsight ? (
            <span className="dealer-portal-inventory-card__insight">
              <RiskLevelPill riskLevel={riskInsight.riskLevel} />
            </span>
          ) : null}
        </div>

        <div className="dealer-portal-inventory-card__footer">
          <Link className="dealer-portal-inventory-card__action primary" to={`/v/automap/dealer/inventario/${listing.id}`}>
            {t("common.view")}
          </Link>
          <span className="dealer-portal-inventory-card__action-sep" aria-hidden="true">
            |
          </span>
          <Link className="dealer-portal-inventory-card__action" to={`/v/automap/dealer/inventario/${listing.id}/editar`}>
            {t("common.edit")}
          </Link>
          {listing.status === "active" ? (
            <>
              <span className="dealer-portal-inventory-card__action-sep" aria-hidden="true">
                |
              </span>
              <DealerListingPublicAction
                listing={listing}
                locale={locale}
                t={t}
                dealerId={dealerId}
                className="dealer-portal-inventory-card__action dealer-portal-inventory-card__action--public"
                label={t("inventory.viewPublic")}
                showUnavailableHint={false}
                icon=""
              />
            </>
          ) : null}
          <span className="dealer-portal-inventory-card__action-sep" aria-hidden="true">
            |
          </span>
          <button type="button" className="dealer-portal-inventory-card__action" onClick={() => onAction(listing.id, "confirm_available")}>
            {t("inventory.confirm")}
          </button>
          <span className="dealer-portal-inventory-card__action-sep" aria-hidden="true">
            |
          </span>
          <button type="button" className="dealer-portal-inventory-card__action" onClick={() => onAction(listing.id, "mark_reserved")}>
            {t("inventory.reserve")}
          </button>
          <span className="dealer-portal-inventory-card__action-sep" aria-hidden="true">
            |
          </span>
          <button type="button" className="dealer-portal-inventory-card__action" onClick={() => onAction(listing.id, "mark_sold")}>
            {t("inventory.soldAction")}
          </button>
          <span className="dealer-portal-inventory-card__action-sep" aria-hidden="true">
            |
          </span>
          <button type="button" className="dealer-portal-inventory-card__action" onClick={() => onAction(listing.id, "pause")}>
            {t("inventory.pause")}
          </button>
          <span className="dealer-portal-inventory-card__action-sep" aria-hidden="true">
            |
          </span>
          <button type="button" className="dealer-portal-inventory-card__action" onClick={() => onDuplicate(listing.id)}>
            {t("inventory.duplicate")}
          </button>
        </div>
      </div>
    </article>
  );
}
